import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useContext,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import SideDrawer from '../../../SideDrawer';
import {
  DataRoomNode,
  DATA_ROOM_NODE_1_T_1_TAGS,
  DATA_ROOM_NODE_TAG,
  DATA_ROOM_NODE_FULL_TAGS,
  DataRoomNodeTag,
} from 'router/subrouters/DataRoom/models/DataRoomNode';
import { useTranslation } from 'react-i18next';
import { Loader, RadioGroup } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import FileInfo from '../DataRoomDocumentDrawers/ManageFileAccess/components/FileInfo';
import confirm from 'modules/confirm';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getIsNodeFolder } from 'utils';
import useUndoActions from 'hooks/useUndoActions';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';

import './DrNodeTagDrawer.styles.scss';

type DrNodeTagDrawerProps = {
  className?: string;
  node: DataRoomNode;
  onSuccessTag: (node: DataRoomNode) => void;
  removeTagFromOldNode: (nodeId: string) => void;
};

const DrNodeTagDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  DrNodeTagDrawerProps
> = (props, ref) => {
  const { className, node, onSuccessTag, removeTagFromOldNode } = props;

  const [drawerRef, setDrawerRef] = useCallbackRef<SideDrawerRef>();
  const [isLoading, setIsLoading] = useState(false);
  const [oldTag, setOldTag] = useState<DataRoomNodeTag>(null);

  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const classes = classNames('dr-node-tag-drawer', className);

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  useImperativeHandle(ref, () => drawerRef, [drawerRef]);

  const options = useMemo(
    () => [
      ...Object.values(DATA_ROOM_NODE_TAG).map((key) => ({
        value: key,
        label: t(DATA_ROOM_NODE_FULL_TAGS[key]),
      })),
      { value: null, label: t('nullTagLabel') },
    ],
    [t],
  );

  const onUndo = useCallback(async () => {
    try {
      clearUndoTimeout();
      onSuccessTag({ ...node, tag: oldTag });

      await dataRoomApi.nodes.tagDataRoomNode(node._id, oldTag || null);
    } catch {
      return;
    }
  }, [node, oldTag, onSuccessTag, clearUndoTimeout]);

  const onSubmit = useCallback(
    async (tag) => {
      try {
        let oldNodeIdToRemove = null;
        clearUndoTimeout();

        // Only check for existing tag if its a 1_T_1
        if (DATA_ROOM_NODE_1_T_1_TAGS.includes(tag)) {
          setIsLoading(true);

          const {
            data: { dataRoomNode: oldNode },
          } = await dataRoomApi.nodes.getTaggedNode(node._id, tag);

          if (oldNode) {
            const confirmed = await confirm(
              {
                title: t('thisTaggedIsUsed'),
                content: t('thisTaggedIsUsedMessage'),
              },
              true,
            );

            // Do nothing
            if (!confirmed) {
              setIsLoading(false);
              return;
            }

            oldNodeIdToRemove = oldNode._id;
          }
        }

        const newTag = tag || null;

        setOldTag(node.tag);
        setUndoTimeout();
        onSuccessTag({ ...node, tag: newTag });
        setIsLoading(false);

        if (oldNodeIdToRemove) {
          removeTagFromOldNode(oldNodeIdToRemove);
        }

        await dataRoomApi.nodes.tagDataRoomNode(node._id, newTag);
      } catch (e) {
        popServerError(e);
        setIsLoading(false);
        onUndo();
      }
    },
    [
      node,
      onSuccessTag,
      t,
      removeTagFromOldNode,
      popServerError,
      onUndo,
      setUndoTimeout,
      clearUndoTimeout,
    ],
  );

  if (getIsNodeFolder(node)) return null;

  return (
    <>
      <SideDrawer
        ref={setDrawerRef}
        className={classes}
        title={t('tagFile')}
        lazyContent
        closeAction={clearUndoTimeout}
      >
        <FileInfo nodes={[node]} />

        <p>{t('classifyWithTag')}</p>

        {isLoading && <Loader />}

        <RadioGroup
          name="tag"
          value={node?.tag}
          options={options}
          onChange={(e) => onSubmit(e.target.value)}
        />
      </SideDrawer>

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={isLoading}
        onUndo={onUndo}
      >
        <p>{t('successTagMessage')}</p>
      </PopNotificationWithUndo>
    </>
  );
};

export default forwardRef(DrNodeTagDrawer);
